import { onlineManager } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';
const PROBE_INTERVAL_MS = 30_000;
const PROBE_TIMEOUT_MS = 3_000;
const PROBE_COOLDOWN_MS = 15_000;

let lastProbeAt = 0;

/**
 * `navigator.onLine` lies on captive-portal Wi-Fi; this probe is the authority.
 * Returns true only if `/healthcheck` answers under PROBE_TIMEOUT_MS.
 */
async function probe(): Promise<boolean> {
  if (!navigator.onLine) return false;
  const now = Date.now();
  if (now - lastProbeAt < PROBE_COOLDOWN_MS) return onlineManager.isOnline();
  lastProbeAt = now;
  try {
    const res = await fetch(`${API_URL}/healthcheck`, {
      method: 'HEAD',
      cache: 'no-store',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Installs the online/offline listeners plus a periodic /healthcheck probe.
 * Call once at app boot (after the QueryClient is created).
 */
export function startNetworkWatcher(): () => void {
  const refresh = () => {
    void probe().then((ok) => onlineManager.setOnline(ok));
  };

  window.addEventListener('online', refresh);
  window.addEventListener('offline', () => onlineManager.setOnline(false));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refresh();
  });
  const interval = window.setInterval(refresh, PROBE_INTERVAL_MS);
  refresh();

  return () => {
    window.removeEventListener('online', refresh);
    window.clearInterval(interval);
  };
}
